<template>
  <t-modal
    ref="pageModal"
    v-model="isShownModal"
    :classes="modalClasses"
    :class="[ourModalClasses, modalWidthClass]"
    :click-to-close="clickToClose"
    :esc-to-close="escToClose"
    :data-cy="dataCy"
    hide-close-button
    @closed="onCloseModal"
  >
    <div class="our-modal">
      <div class="our-modal-header">
        <div class="our-modal-header-left-wrapper">
          <!-- @slot Use it to add something to header left before. -->
          <slot name="header-left-before" />

          <!-- @slot Use it to add something to header left. -->
          <slot name="header-left">
            <ArrowLink
              v-if="isShownArrowButton"
              :back-route="backRoute"
              :data-cy="`${dataCy}-back`"
              @click="onClickBack"
            />

            <div>
              <OurHeader size="md" :color="color" :text="title" />
              <div v-if="description" class="our-modal-header-subtitle">
                {{ description }}
              </div>
            </div>
          </slot>

          <!-- @slot Use it to add something to header left after. -->
          <slot name="header-left-after" />
        </div>

        <!-- @slot Use it to add something to header right. -->
        <slot name="header-right">
          <OurSvgIcon
            v-if="showCloseIcon"
            :src="icons.Close"
            class="icon"
            :data-cy="`${dataCy}-close`"
            @click="onCloseModal"
          />
        </slot>
      </div>

      <div class="our-modal-body">
        <!-- @slot Use it to add something to modal body. -->
        <slot />
      </div>
    </div>

    <OurDivider v-if="!noDivider" no-border />

    <template v-if="isExistFooter" #footer>
      <OurDivider v-if="!noDivider" variant="dark" no-bottom-padding no-top-padding />

      <div class="our-modal-footer" :class="reverseFooterClass">
        <div v-if="isExistSlot('footer-left')" class="our-modal-footer-left">
          <!-- @slot Use it to add something to footer left. -->
          <slot name="footer-left" />
        </div>

        <div v-if="isExistSlot('footer-right')" class="our-modal-footer-right">
          <!-- @slot Use it to add something to footer right. -->
          <slot name="footer-right" />
        </div>
      </div>
    </template>
  </t-modal>
</template>

<script>
import { getThemeClass } from "@/services/_bit/UiService";
import { mapGetters } from "vuex";

import ArrowLink from "./components/ArrowLink";
import TModal from "vue-tailwind/dist/t-modal";
import OurSvgIcon from "@/components/_bit/IconSvg";
import OurHeader from "@/components/_bit/TextHeader";
import OurDivider from "@/components/_bit/Divider";

export default {
  name: "OurModal",

  components: {
    ArrowLink,
    TModal,
    OurSvgIcon,
    OurHeader,
    OurDivider,
  },

  props: {
    /**
     * Set width for modal.
     * @values xs, sm, md, lg, xl, 2xl, 3xl, 4xl, 5xl
     */
    width: {
      type: String,
      default: "sm",
    },

    /**
     * Set modal's title.
     */
    title: {
      type: String,
      default: "",
    },

    /**
     * Sets page description.
     */
    description: {
      type: String,
      default: "",
    },

    /**
     * Set back route params (name, title, params).
     */
    backRoute: {
      type: Object,
      default: () => ({}),
    },

    /**
     * Make active click for close.
     */
    clickToClose: {
      type: Boolean,
      default: true,
    },

    /**
     * Make close icon active.
     */
    showCloseIcon: {
      type: Boolean,
      default: true,
    },

    /**
     * Make active ESC for close.
     */
    escToClose: {
      type: Boolean,
      default: true,
    },

    /**
     * Change modal state (hidden / shown).
     */
    value: {
      type: Boolean,
      default: false,
    },

    /**
     * The color of the title.
     * @values gray, red, orange, yellow, green, blue, violet, fuchsia
     */
    color: {
      type: String,
      default: "accent",
    },

    /**
     * Add extra top margin for modal inside modal.
     */
    inner: {
      type: Boolean,
      default: false,
    },

    /**
     * Removes divider between end of content end footer.
     */
    noDivider: {
      type: Boolean,
      default: false,
    },

    /**
     * Reverse left and right footer blocks (in a mobile version only).
     */
    mobileFooterReverse: {
      type: Boolean,
      default: false,
    },

    /**
     * Sets data-cy attribute for correct element sampling in tests.
     */
    dataCy: {
      type: String,
      default: "",
    },
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),

    icons: () => ({
      Close: require("@/components/_bit/IconSvg/icons/Close.svg"),
    }),

    ourModalClasses() {
      const theme = getThemeClass();
      const inner = this.inner ? "our-modal-inner" : "";

      return [theme, inner];
    },

    modalWidthClass() {
      return {
        "our-modal-size-xs": this.width === "xs", // 400px
        "our-modal-size-sm": this.width === "sm", //   500px
        "our-modal-size-md": this.width === "md", //   600px
        "our-modal-size-lg": this.width === "lg", //   700px
        "our-modal-size-xl": this.width === "xl", //   800px
        "our-modal-size-2xl": this.width === "2xl", // 900px
        "our-modal-size-3xl": this.width === "3xl", // 1000px
        "our-modal-size-4xl": this.width === "4xl", // 1100px
        "our-modal-size-5xl": this.width === "5xl", // 1200px
      };
    },

    modalClasses() {
      return {
        overlay: "!mt-0 z-50 bg-black bg-opacity-50 outline-none",
        wrapper: `flex justify-center flex-col z-50 mx-3 my-12 our-modal-wrapper`,
        modal: `t-modal bg-white shadow rounded-2xl p-0`,
        footer: "rounded-b-2xl",
        overlayEnterClass: "opacity-0",
        overlayEnterActiveClass: "transition ease-out duration-300",
        overlayEnterToClass: "opacity-100",
        overlayLeaveClass: "opacity-100",
        overlayLeaveActiveClass: "transition ease-in duration-200",
        overlayLeaveToClass: "opacity-0",
        enterClass: "opacity-0 translate-y-4 sm:translate-y-0 transform sm:scale-95",
        enterActiveClass: "transition ease-out duration-300",
        enterToClass: "opacity-100 translate-y-0 transform sm:scale-100",
        leaveClass: "opacity-100 translate-y-0 transform sm:scale-100",
        leaveActiveClass: "transition ease-in duration-200",
        leaveToClass: "opacity-0 translate-y-4 sm:translate-y-0 transform sm:scale-95",
      };
    },

    reverseFooterClass() {
      return {
        "reverse-footer": this.mobileFooterReverse && this.isMobileDevice,
      };
    },

    headerTitleClass() {
      return Object.keys(this.backRoute).length ? "padding-left" : "";
    },

    isShownModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },

    isShownArrowButton() {
      return !!Object.keys(this.backRoute).length;
    },

    isExistFooter() {
      return this.isExistSlot("footer-left") || this.isExistSlot("footer-right");
    },
  },

  methods: {
    showModal() {
      this.isShownModal = true;
    },

    closeModal() {
      this.isShownModal = false;
    },

    onCloseModal() {
      this.closeModal();
    },

    onClickBack() {
      this.$emit("clickBack");
    },

    isExistSlot(slotName) {
      return !!this.$scopedSlots[slotName];
    },
  },
};
</script>

<style lang="postcss" scoped>
.our-modal {
  @apply rounded-t-2xl;

  &-header {
    @apply flex items-start justify-between;
    @apply px-8 pb-6 pt-8;

    &-left {
      &-wrapper {
        @apply flex items-center space-x-4;
      }
    }

    &-title {
      @apply text-3xl font-medium text-gray-900;
    }

    .icon {
      @apply cursor-pointer;
    }

    &-subtitle {
      @apply mt-1.5 text-base font-normal text-gray-600;
    }
  }

  &-body {
    @apply space-y-4;
    @apply px-8;
  }

  &-footer {
    @apply flex justify-between px-8 py-8;

    &-left,
    &-right {
      @apply flex flex-col md:flex-row;
      @apply space-y-4 md:space-x-4 md:space-y-0;
      @apply w-full;
    }

    &-right {
      @apply justify-end;
    }
  }
}

.padding-left {
  @apply pl-2;
}

.our-modal-size-xs:deep() {
  > .our-modal-wrapper {
    @apply md:w-100;
  }
}

.our-modal-size-sm:deep() {
  > .our-modal-wrapper {
    @apply md:w-125;
  }
}

.our-modal-size-md:deep() {
  > .our-modal-wrapper {
    @apply md:w-150;
  }
}

.our-modal-size-lg:deep() {
  > .our-modal-wrapper {
    @apply md:w-175;
  }
}

.our-modal-size-xl:deep() {
  > .our-modal-wrapper {
    @apply md:w-200;
  }
}

.our-modal-size-2xl:deep() {
  > .our-modal-wrapper {
    @apply md:w-225;
  }
}

.our-modal-size-3xl:deep() {
  > .our-modal-wrapper {
    @apply md:w-250;
  }
}

.our-modal-size-4xl:deep() {
  > .our-modal-wrapper {
    @apply w-full xl:w-275;
  }
}

.our-modal-size-5xl:deep() {
  > .our-modal-wrapper {
    @apply md:w-3/4;
  }
}

.our-modal-inner:deep() {
  > .our-modal-wrapper {
    @apply !my-[4.5rem];
  }
}

.reverse-footer {
  @apply flex flex-col space-y-4;

  .page-footer-right {
    @apply flex flex-col-reverse space-y-reverse;
  }
}
</style>
